"use client";

import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { NumberTicker } from "@/components/ui/number-ticker";
import { PageHeader } from "@/components/page-header";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { DownloadIcon } from "@/components/ui/download";
import { CalendarIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { useCallback, useEffect, useState } from "react";
import { Skeleton, AnalyticsSkeleton } from "@/components/ui/skeleton";

// ─── Types ────────────────────────────────────────────────────────────────────

type Range = "7d" | "14d" | "30d" | "90d" | "all" | "custom";

type TrendPoint = {
  key: string;
  label: string;
  messages: number;
  chats: number;
  activeUsers: number;
  meetings: number;
};
type TokenTrendPoint = { key: string; label: string; tokens: number };
type AgentTrendPoint = { key: string; label: string; agents: number; knowledge: number; custom: number };
type ModelPoint = { model: string; count: number };
type BandUser = { name: string; count: number };
type QuartilePoint = { label: string; userCount: number; avg: number; users: BandUser[] };
type FbTrendPoint = { key: string; label: string; positive: number; negative: number };
type FeedbackSection = { sentiment: { positive: number; negative: number }; trend: FbTrendPoint[] };
type TopTokenUser = { name: string; email: string | null; tokens: number };

type ManagedAgentEntry = {
  id: string;
  name: string;
  archived: boolean;
  owner: string;
  tokens: number;
  sessions: number;
  projects: string[];
};
type ManagedAgentsData = {
  limit: number;
  agentCount: number;
  sessionCount: number;
  topAgents: ManagedAgentEntry[];
};

type CostWorkspace = "all" | "dev" | "prod";
type CostDay = { date: string; label: string; cost: number; dev?: number; prod?: number };
type CostData = {
  range: Range;
  workspace: CostWorkspace;
  days: CostDay[];
  total: number;
  currency: string;
  workspacesConfigured: { dev: boolean; prod: boolean };
};

type AnalyticsData = {
  range: Range;
  bucketType: string;
  totalUsers: number;
  newUsers: number;
  activeUsers: number;
  totalChats: number;
  chatsInPeriod: number;
  kaChatsInPeriod?: number;
  meetingsInPeriod?: number;
  messagesInPeriod: number;
  avgMessagesPerChat: number;
  totalChatTokens: number;
  chatTokensTrend: TokenTrendPoint[];
  activityTrend: TrendPoint[];
  agentsTrend?: AgentTrendPoint[];
  modelUsage: ModelPoint[];
  agents: { total: number; inPeriod: number; knowledge: number; custom: number };
  prompts: { total: number; inPeriod: number };
  tools: { total: number; inPeriod: number };
  files: { total: number; totalTokens: number; inPeriod: number };
  messageQuartileDistribution: QuartilePoint[];
  meetingQuartileDistribution: QuartilePoint[];
  messageFeedback: FeedbackSection;
  meetingFeedback: FeedbackSection;
  topTokenUsers?: TopTokenUser[];
  journeyLevels?: { explorer: number; practitioner: number; builder: number; champion: number };
};

type AnalyticsPanelProps = {
  onBack?: () => void;
  canViewTopTokenUsers?: boolean;
  canViewManagedAgents?: boolean;
};

// ─── Constants ─────────────────────────────────────────────────────────────────

const RANGES: { value: Range; label: string }[] = [
  { value: "7d", label: "Last 7 days" },
  { value: "14d", label: "Last 2 weeks" },
  { value: "30d", label: "Last month" },
  { value: "90d", label: "Last 3 months" },
  { value: "all", label: "All time" },
];

const CHART_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];

// ─── Chart configs ─────────────────────────────────────────────────────────────

type TrendMetric = "messages" | "chats" | "activeUsers" | "meetings";

const TREND_METRICS: { value: TrendMetric; label: string }[] = [
  { value: "messages", label: "Messages" },
  { value: "chats", label: "Chats" },
  { value: "activeUsers", label: "Active users" },
  { value: "meetings", label: "Meeting minutes" },
];

const activityChartConfig = {
  messages:    { label: "Messages",       color: "var(--chart-1)" },
  chats:       { label: "Chats",          color: "var(--chart-3)" },
  activeUsers: { label: "Active users",   color: "var(--chart-2)" },
  meetings:    { label: "Meeting minutes", color: "var(--chart-4)" },
} satisfies ChartConfig;

const quartileMsgConfig = {
  avg: { label: "Avg messages / user", color: "var(--chart-1)" },
} satisfies ChartConfig;

const quartileMeetConfig = {
  avg: { label: "Avg meetings / user", color: "var(--chart-3)" },
} satisfies ChartConfig;

const agentTypesChartConfig = {
  knowledge: { label: "Knowledge", color: "var(--chart-2)" },
  custom:    { label: "Custom",    color: "var(--chart-4)" },
} satisfies ChartConfig;

const modelChartConfig = {
  count: { label: "Chats", color: "var(--chart-1)" },
} satisfies ChartConfig;

const feedbackChartConfig = {
  positive: { label: "Positive", color: "var(--chart-1)" },
  negative: { label: "Negative", color: "var(--destructive)" },
} satisfies ChartConfig;

const chatTokensChartConfig = {
  tokens: { label: "Total tokens", color: "var(--chart-2)" },
} satisfies ChartConfig;

const costChartConfig = {
  cost: { label: "Cost (USD)", color: "var(--chart-5)" },
  dev:  { label: "Development", color: "var(--chart-2)" },
  prod: { label: "Production",  color: "var(--chart-4)" },
} satisfies ChartConfig;

const COST_WORKSPACE_OPTIONS: { value: CostWorkspace; label: string }[] = [
  { value: "all", label: "All workspaces" },
  { value: "dev", label: "Woods AI — Development" },
  { value: "prod", label: "Woods AI — Production" },
];

const MANAGED_AGENT_LIMITS = [5, 10, 20, 30] as const;

function formatModelName(model: string): string {
  const slash = model.lastIndexOf("/");
  return slash >= 0 ? model.slice(slash + 1) : model;
}

function exportActivityTrendCsv(rows: TrendPoint[], rangeLabel: string) {
  const headers = ["Period", "Messages", "Chats", "Active users", "Meeting minutes"];
  const csvRows = rows.map((r) =>
    [`"${r.label.replace(/"/g, '""')}"`, r.messages, r.chats, r.activeUsers, r.meetings].join(",")
  );
  const csv = [headers.join(","), ...csvRows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `activity-trend-${rangeLabel}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ─── Quartile tooltip ─────────────────────────────────────────────────────────

function QuartileTooltip({
  active,
  payload,
  metricLabel,
}: {
  active?: boolean;
  payload?: Array<{ payload: QuartilePoint }>;
  metricLabel: string;
}) {
  if (!active || !payload?.length) return null;
  const { label, avg, users } = payload[0].payload;
  return (
    <div className="rounded-xl border border-border bg-card px-3 py-2.5 text-xs shadow-md max-w-[220px]">
      <p className="mb-1.5 font-medium text-foreground">{label}</p>
      <p className="mb-2 text-muted-foreground">
        {metricLabel}: <span className="font-medium text-foreground">{avg.toLocaleString()}</span>
      </p>
      {users.length > 0 && (
        <div className="space-y-1 border-t border-border pt-2">
          {users.map((u, i) => (
            <div key={i} className="flex items-center justify-between gap-3">
              <span className="truncate text-muted-foreground">{u.name}</span>
              <span className="shrink-0 font-medium text-foreground">{u.count.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── KPI card ─────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  sub,
  decimalPlaces = 0,
}: {
  label: string;
  value: number;
  sub?: string;
  decimalPlaces?: number;
}) {
  return (
    <Card size="sm" className="gap-3">
      <CardContent className="pt-4">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="mt-1 text-3xl font-semibold text-foreground tabular-nums">
          <NumberTicker value={value} decimalPlaces={decimalPlaces} />
        </p>
        {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export function AnalyticsPanel({ onBack, canViewTopTokenUsers, canViewManagedAgents }: AnalyticsPanelProps) {
  const [range, setRange] = useState<Range>("30d");
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trendMetrics, setTrendMetrics] = useState<TrendMetric[]>(["messages", "chats"]);

  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const [costWorkspace, setCostWorkspace] = useState<CostWorkspace>("all");
  const [costData, setCostData] = useState<CostData | null>(null);
  const [costLoading, setCostLoading] = useState(true);
  const [costError, setCostError] = useState<string | null>(null);
  const [costCurrency, setCostCurrency] = useState<"USD" | "NZD">("USD");

  const [managedLimit, setManagedLimit] = useState<number>(5);
  const [managedData, setManagedData] = useState<ManagedAgentsData | null>(null);
  const [managedLoading, setManagedLoading] = useState(true);
  const [managedError, setManagedError] = useState<string | null>(null);

  const NZD_RATE = 1.7;
  const costFx = (usd: number) => costCurrency === "NZD" ? usd * NZD_RATE : usd;
  const costSymbol = costCurrency === "NZD" ? "NZ$" : "$";

  const load = useCallback(async (r: Range, dateRange?: DateRange) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      let url = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/get-analytics`;
      if (r === "custom" && dateRange?.from) {
        const from = dateRange.from.toISOString();
        const to = (dateRange.to ?? dateRange.from).toISOString();
        url += `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
      } else {
        url += `?range=${r}`;
      }
      const res = await fetch(url, {
        headers: {
          apikey: process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? "",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
      });

      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error ?? "Failed to load analytics.");
      setData(json as AnalyticsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load analytics.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCost = useCallback(async (r: Range, ws: CostWorkspace, dateRange?: DateRange) => {
    setCostLoading(true);
    setCostError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      let url = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/get-cost-report?workspace=${ws}`;
      if (r === "custom" && dateRange?.from) {
        const from = dateRange.from.toISOString();
        const to = (dateRange.to ?? dateRange.from).toISOString();
        url += `&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
      } else {
        url += `&range=${r}`;
      }
      const res = await fetch(url, {
        headers: {
          apikey: process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? "",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error ?? "Failed to load cost data.");
      setCostData(json as CostData);
    } catch (err) {
      setCostError(err instanceof Error ? err.message : "Unable to load cost data.");
    } finally {
      setCostLoading(false);
    }
  }, []);

  const loadManagedAgents = useCallback(async (limit: number) => {
    setManagedLoading(true);
    setManagedError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const url = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/get-managed-agent-analytics?limit=${limit}`;
      const res = await fetch(url, {
        headers: {
          apikey: process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? "",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error ?? "Failed to load managed agent data.");
      setManagedData(json as ManagedAgentsData);
    } catch (err) {
      setManagedError(err instanceof Error ? err.message : "Unable to load managed agent data.");
    } finally {
      setManagedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (range === "custom") return; // only load custom when the user explicitly applies it
    load(range);
  }, [load, range]);

  useEffect(() => {
    if (range === "custom") return;
    loadCost(range, costWorkspace);
  }, [loadCost, range, costWorkspace]);

  useEffect(() => {
    if (canViewManagedAgents) loadManagedAgents(managedLimit);
  }, [canViewManagedAgents, loadManagedAgents, managedLimit]);

  const periodLabel =
    range === "custom" && customDateRange?.from
      ? customDateRange.to
        ? `${customDateRange.from.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${customDateRange.to.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
        : customDateRange.from.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
      : RANGES.find((r) => r.value === range)?.label.toLowerCase() ?? "";

  const showMessagesAxis = trendMetrics.includes("messages");
  const trendBarMetrics = trendMetrics.filter((m) => m === "chats"); // bar (right axis) alongside messages
  const trendLineMetrics = trendMetrics.filter((m) => m === "activeUsers" || m === "meetings");
  const showTrendRightAxis = trendBarMetrics.length > 0 || trendLineMetrics.length > 0;

  if (loading && !data) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
        <PageHeader title="Analytics" description="Platform usage at a glance" onBack={onBack} />
        <AnalyticsSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
        <PageHeader title="Analytics" description="Platform usage at a glance" onBack={onBack} />
        <div className="flex flex-1 items-center justify-center px-6">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;


  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader
        title="Analytics"
        description="Platform usage at a glance"
        onBack={onBack}
        actions={
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <ToggleGroup
              type="single"
              value={range === "custom" ? "" : range}
              onValueChange={(v) => {
                if (!v) return;
                setCustomDateRange(undefined);
                setRange(v as Range);
              }}
              variant="outline"
              size="sm"
              className="flex-wrap"
            >
              {RANGES.map(({ value, label }) => (
                <ToggleGroupItem key={value} value={value}>
                  {label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>

            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={range === "custom" ? "default" : "outline"}
                  size="sm"
                  className="h-8 gap-1.5 px-3 text-xs"
                >
                  <CalendarIcon className="size-3.5" />
                  {range === "custom" && customDateRange?.from ? periodLabel : "Custom"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={customDateRange}
                  onSelect={setCustomDateRange}
                  numberOfMonths={2}
                  disabled={{ after: new Date() }}
                  captionLayout="dropdown"
                />
                <div className="flex justify-end gap-2 border-t border-border px-3 py-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => {
                      setCustomDateRange(undefined);
                      setCalendarOpen(false);
                      setRange("30d");
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    disabled={!customDateRange?.from}
                    onClick={() => {
                      setCalendarOpen(false);
                      setRange("custom");
                      load("custom", customDateRange);
                      loadCost("custom", costWorkspace, customDateRange);
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        }
      />

      <div className="flex-1 overflow-auto">
        <div className="space-y-8 px-6 py-6">

          {/* ── Section 2: KPI cards ───────────────────────────────────────── */}
          {(() => {
            const chatsPerUser = data.activeUsers > 0 ? Math.round((data.chatsInPeriod / data.activeUsers) * 10) / 10 : 0;
            const kaChatsPerUser = data.activeUsers > 0 ? Math.round(((data.kaChatsInPeriod ?? 0) / data.activeUsers) * 10) / 10 : 0;
            const meetingsPerUser = data.activeUsers > 0 ? Math.round(((data.meetingsInPeriod ?? 0) / data.activeUsers) * 10) / 10 : 0;
            return (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                <KpiCard
                  label="Active users"
                  value={data.activeUsers}
                  sub={`of ${data.totalUsers} total · ${periodLabel}`}
                />
                <KpiCard
                  label="Chats / user"
                  value={chatsPerUser}
                  decimalPlaces={1}
                  sub={periodLabel}
                />
                <KpiCard
                  label="KA chats / user"
                  value={kaChatsPerUser}
                  decimalPlaces={1}
                  sub={periodLabel}
                />
                <KpiCard
                  label="Msg / chat"
                  value={data.avgMessagesPerChat}
                  decimalPlaces={1}
                  sub={periodLabel}
                />
                <KpiCard
                  label="Meetings / user"
                  value={meetingsPerUser}
                  decimalPlaces={1}
                  sub={periodLabel}
                />
                <KpiCard
                  label="Agents"
                  value={data.agents.total}
                  sub={`+${data.agents.inPeriod} ${periodLabel}`}
                />
              </div>
            );
          })()}

          {/* ── Section 2b: AI Journey level distribution ──────────────────── */}
          {data.journeyLevels && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle>AI Journey levels</CardTitle>
                <CardDescription>
                  Lifetime adoption levels across all users, updated nightly.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  {(
                    [
                      ["Explorer", data.journeyLevels.explorer],
                      ["Practitioner", data.journeyLevels.practitioner],
                      ["Builder", data.journeyLevels.builder],
                      ["Champion", data.journeyLevels.champion],
                    ] as const
                  ).map(([label, count]) => {
                    const share = data.totalUsers > 0 ? count / data.totalUsers : 0;
                    return (
                      <div key={label} className="rounded-xl border border-border px-4 py-3">
                        <p className="text-xs text-muted-foreground">{label}</p>
                        <p className="mt-1 text-xl font-semibold tabular-nums text-foreground">
                          {count}
                        </p>
                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-brand"
                            style={{ width: `${Math.round(share * 100)}%` }}
                          />
                        </div>
                        <p className="mt-1 text-xs tabular-nums text-muted-foreground">
                          {Math.round(share * 100)}% of users
                        </p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── Section 3: Activity trend ──────────────────────────────────── */}
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 pb-2">
              <CardTitle>Activity trend</CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <ToggleGroup
                  type="multiple"
                  value={trendMetrics}
                  onValueChange={(v) => v.length > 0 && setTrendMetrics(v as TrendMetric[])}
                  variant="outline"
                  size="sm"
                  className="flex-wrap"
                >
                  {TREND_METRICS.map(({ value, label }) => (
                    <ToggleGroupItem key={value} value={value} className="text-xs">
                      {label}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 px-2.5 text-xs"
                  disabled={data.activityTrend.length === 0}
                  onClick={() => exportActivityTrendCsv(data.activityTrend, range)}
                >
                  <DownloadIcon size={14} />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {data.activityTrend.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No data for this period.
                </p>
              ) : (
                <ChartContainer config={activityChartConfig} className="h-[240px] aspect-auto">
                  <ComposedChart
                    data={data.activityTrend}
                    margin={{ top: 4, right: showTrendRightAxis ? 16 : 8, bottom: 0, left: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--border)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    {showMessagesAxis && (
                      <YAxis
                        yAxisId="messages"
                        tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                        axisLine={false}
                        tickLine={false}
                        width={36}
                      />
                    )}
                    {showTrendRightAxis && (
                      <YAxis
                        yAxisId="chats"
                        orientation={showMessagesAxis ? "right" : "left"}
                        tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                        axisLine={false}
                        tickLine={false}
                        width={36}
                      />
                    )}
                    <ChartTooltip content={<ChartTooltipContent />} />
                    {showMessagesAxis && (
                      <Bar
                        yAxisId="messages"
                        dataKey="messages"
                        fill="var(--color-messages)"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={40}
                      />
                    )}
                    {trendBarMetrics.map((metric) => (
                      <Bar
                        key={metric}
                        yAxisId="chats"
                        dataKey={metric}
                        fill={`var(--color-${metric})`}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={40}
                      />
                    ))}
                    {trendLineMetrics.map((metric) => (
                      <Line
                        key={metric}
                        yAxisId="chats"
                        dataKey={metric}
                        stroke={`var(--color-${metric})`}
                        strokeWidth={2}
                        dot={false}
                        type="monotone"
                      />
                    ))}
                  </ComposedChart>
                </ChartContainer>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                {trendMetrics.map((metric) => (
                  <span key={metric} className="flex items-center gap-1.5">
                    <span
                      className={
                        metric === "messages" || metric === "chats"
                          ? "size-2.5 rounded-sm"
                          : "size-2.5 rounded-full"
                      }
                      style={{ background: activityChartConfig[metric].color }}
                    />
                    {activityChartConfig[metric].label}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* ── Section 4: Distribution of Usage ─────────────────────────── */}
          <div className="grid grid-cols-2 gap-4">
            {/* Messages quartile */}
            <Card>
              <CardHeader>
                <CardTitle>Message distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {data.messageQuartileDistribution.every((q) => q.userCount === 0) ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">No data for this period.</p>
                ) : (
                  <>
                    <ChartContainer config={quartileMsgConfig} className="h-[200px] aspect-auto">
                      <BarChart data={data.messageQuartileDistribution} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} width={36} />
                        <ChartTooltip content={<QuartileTooltip metricLabel="Avg messages / user" />} />
                        <Bar dataKey="avg" fill="var(--color-avg)" radius={[4, 4, 0, 0]} maxBarSize={48} />
                      </BarChart>
                    </ChartContainer>
                    <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                      {data.messageQuartileDistribution.map((q) => (
                        <p key={q.label} className="text-xs text-muted-foreground">
                          {q.userCount} {q.userCount === 1 ? "user" : "users"}
                        </p>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Meetings quartile */}
            <Card>
              <CardHeader>
                <CardTitle>Meeting distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {data.meetingQuartileDistribution.every((q) => q.userCount === 0) ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">No data for this period.</p>
                ) : (
                  <>
                    <ChartContainer config={quartileMeetConfig} className="h-[200px] aspect-auto">
                      <BarChart data={data.meetingQuartileDistribution} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} width={36} />
                        <ChartTooltip content={<QuartileTooltip metricLabel="Avg meetings / user" />} />
                        <Bar dataKey="avg" fill="var(--color-avg)" radius={[4, 4, 0, 0]} maxBarSize={48} />
                      </BarChart>
                    </ChartContainer>
                    <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                      {data.meetingQuartileDistribution.map((q) => (
                        <p key={q.label} className="text-xs text-muted-foreground">
                          {q.userCount} {q.userCount === 1 ? "user" : "users"}
                        </p>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ── Section 5: Agent types + Model usage ─────────────────────── */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Agent types</CardTitle>
              </CardHeader>
              <CardContent>
                {data.agents.total === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    No agents created yet.
                  </p>
                ) : (
                  <>
                    <ChartContainer config={agentTypesChartConfig} className="h-[180px] aspect-auto">
                      <PieChart>
                        <Pie
                          data={[
                            { name: "knowledge", value: data.agents.knowledge },
                            { name: "custom", value: data.agents.custom },
                          ]}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={44}
                          outerRadius={70}
                          paddingAngle={3}
                        >
                          <Cell fill="var(--color-knowledge)" />
                          <Cell fill="var(--color-custom)" />
                        </Pie>
                        <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                      </PieChart>
                    </ChartContainer>
                    <div className="mt-2 flex justify-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <span className="size-2.5 rounded-full" style={{ background: "var(--chart-2)" }} />
                        Knowledge ({data.agents.knowledge})
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="size-2.5 rounded-full" style={{ background: "var(--chart-4)" }} />
                        Custom ({data.agents.custom})
                      </span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Model usage</CardTitle>
              </CardHeader>
              <CardContent>
                {data.modelUsage.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    No data for this period.
                  </p>
                ) : (
                  <ChartContainer
                    config={modelChartConfig}
                    className="aspect-auto"
                    style={{ height: Math.max(160, data.modelUsage.length * 36) }}
                  >
                    <BarChart
                      layout="vertical"
                      data={data.modelUsage.map((m) => ({
                        ...m,
                        model: formatModelName(m.model),
                      }))}
                      margin={{ top: 0, right: 16, bottom: 0, left: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="var(--border)"
                        horizontal={false}
                      />
                      <YAxis
                        dataKey="model"
                        type="category"
                        tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                        axisLine={false}
                        tickLine={false}
                        width={140}
                      />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <ChartTooltip content={<ChartTooltipContent nameKey="model" />} />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={22}>
                        {data.modelUsage.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ChartContainer>
                )}
              </CardContent>
            </Card>
          </div>


          {/* ── Section 7: Feedback ───────────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-4">
            {(["messageFeedback", "meetingFeedback"] as const).map((key) => {
              const fb = data[key];
              const total = fb.sentiment.positive + fb.sentiment.negative;
              const pie = total > 0
                ? [{ name: "positive", value: fb.sentiment.positive }, { name: "negative", value: fb.sentiment.negative }]
                : [];
              return (
                <Card key={key}>
                  <CardHeader>
                    <CardTitle>{key === "messageFeedback" ? "Message feedback" : "Meeting feedback"}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {total === 0 && fb.trend.length === 0 ? (
                      <p className="py-8 text-center text-sm text-muted-foreground">No feedback yet.</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        {/* Sentiment donut */}
                        <div>
                          {total === 0 ? (
                            <p className="py-8 text-center text-sm text-muted-foreground">No ratings yet.</p>
                          ) : (
                            <>
                              <ChartContainer config={feedbackChartConfig} className="h-[140px] aspect-auto">
                                <PieChart>
                                  <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={36} outerRadius={58} paddingAngle={3}>
                                    <Cell fill="var(--color-positive)" />
                                    <Cell fill="var(--color-negative)" />
                                  </Pie>
                                  <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                                </PieChart>
                              </ChartContainer>
                              <div className="mt-2 flex justify-center gap-3 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1.5">
                                  <span className="size-2 rounded-full" style={{ background: "var(--chart-1)" }} />
                                  Positive ({fb.sentiment.positive})
                                </span>
                                <span className="flex items-center gap-1.5">
                                  <span className="size-2 rounded-full" style={{ background: "var(--destructive)" }} />
                                  Negative ({fb.sentiment.negative})
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                        {/* Trend */}
                        <div>
                          {fb.trend.length === 0 ? (
                            <p className="py-8 text-center text-sm text-muted-foreground">No trend data.</p>
                          ) : (
                            <ChartContainer config={feedbackChartConfig} className="h-[160px] aspect-auto">
                              <BarChart data={fb.trend} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                                <YAxis tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} width={24} allowDecimals={false} />
                                <ChartTooltip content={<ChartTooltipContent />} />
                                <Bar dataKey="positive" fill="var(--color-positive)" radius={[3, 3, 0, 0]} maxBarSize={20} stackId="fb" />
                                <Bar dataKey="negative" fill="var(--color-negative)" radius={[3, 3, 0, 0]} maxBarSize={20} stackId="fb" />
                              </BarChart>
                            </ChartContainer>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* ── Section 7: Chat tokens trend ─────────────────────────────── */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Total tokens from chats</CardTitle>
              <p className="text-xs text-muted-foreground">
                {data.totalChatTokens.toLocaleString()} tokens {periodLabel}
              </p>
            </CardHeader>
            <CardContent>
              {data.chatTokensTrend.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No data for this period.
                </p>
              ) : (
                <ChartContainer config={chatTokensChartConfig} className="h-55 aspect-auto">
                  <BarChart data={data.chatTokensTrend} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                      axisLine={false}
                      tickLine={false}
                      width={44}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="tokens" fill="var(--color-tokens)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  </BarChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>

          {/* ── Section 8: Top 10 users by token usage (admin+) ──────────── */}
          {canViewTopTokenUsers && data.topTokenUsers && data.topTokenUsers.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle>Top 10 users by token usage</CardTitle>
                <p className="text-xs text-muted-foreground">{periodLabel}</p>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-border">
                  {data.topTokenUsers.map((user, i) => (
                    <div key={i} className="flex items-center gap-3 py-2.5">
                      <span className="w-5 shrink-0 text-xs font-medium text-muted-foreground tabular-nums">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{user.name}</p>
                        {user.email && (
                          <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                        )}
                      </div>
                      <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
                        {user.tokens.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── Section 8.5: Managed agents (superadmin only) ────────────── */}
          {canViewManagedAgents && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-4 pb-2">
                <div>
                  <CardTitle>Managed agents</CardTitle>
                  <p className="text-xs text-muted-foreground">All time · top agents by total tokens used</p>
                </div>
                <Select
                  value={String(managedLimit)}
                  onValueChange={(v) => setManagedLimit(Number(v))}
                >
                  <SelectTrigger className="h-8 w-[110px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MANAGED_AGENT_LIMITS.map((n) => (
                      <SelectItem key={n} value={String(n)} className="text-xs">
                        Top {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent>
                {managedLoading ? (
                  <div className="space-y-3 py-2">
                    <div className="grid grid-cols-2 gap-4">
                      <Skeleton className="h-20 rounded-xl" />
                      <Skeleton className="h-20 rounded-xl" />
                    </div>
                    <Skeleton className="h-40 w-full rounded-xl" />
                  </div>
                ) : managedError ? (
                  <p className="py-8 text-center text-sm text-destructive">{managedError}</p>
                ) : !managedData ? null : (
                  <>
                    <div className="mb-5 grid grid-cols-2 gap-4">
                      <div className="rounded-xl border border-border bg-muted/40 px-4 py-3">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Managed agents</p>
                        <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                          <NumberTicker value={managedData.agentCount} />
                        </p>
                      </div>
                      <div className="rounded-xl border border-border bg-muted/40 px-4 py-3">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Sessions</p>
                        <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                          <NumberTicker value={managedData.sessionCount} />
                        </p>
                      </div>
                    </div>
                    {managedData.topAgents.length === 0 ? (
                      <p className="py-8 text-center text-sm text-muted-foreground">No managed agents yet.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-border text-muted-foreground">
                              <th className="py-2 pr-3 text-left font-normal">#</th>
                              <th className="px-3 py-2 text-left font-normal">Agent</th>
                              <th className="px-3 py-2 text-left font-normal">Owner</th>
                              <th className="px-3 py-2 text-left font-normal">Projects</th>
                              <th className="py-2 pl-3 text-right font-normal">Tokens</th>
                            </tr>
                          </thead>
                          <tbody>
                            {managedData.topAgents.map((agent, i) => (
                              <tr key={agent.id} className="border-b border-border/50">
                                <td className="py-2.5 pr-3 text-muted-foreground tabular-nums">{i + 1}</td>
                                <td className="max-w-[220px] px-3 py-2.5">
                                  <p className="truncate text-sm font-medium text-foreground">
                                    {agent.name}
                                    {agent.archived && (
                                      <span className="ml-1.5 text-xs font-normal text-muted-foreground">(archived)</span>
                                    )}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {agent.sessions.toLocaleString()} {agent.sessions === 1 ? "session" : "sessions"}
                                  </p>
                                </td>
                                <td className="max-w-[160px] truncate px-3 py-2.5 text-muted-foreground">{agent.owner}</td>
                                <td className="max-w-[220px] truncate px-3 py-2.5 text-muted-foreground">
                                  {agent.projects.length > 0 ? agent.projects.join(", ") : "–"}
                                </td>
                                <td className="py-2.5 pl-3 text-right text-sm font-semibold tabular-nums text-foreground">
                                  {agent.tokens.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* ── Section 9: Anthropic cost per day ────────────────────────── */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4 pb-2">
              <div>
                <CardTitle>Cost per day (Anthropic)</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <ToggleGroup
                  type="single"
                  value={costCurrency}
                  onValueChange={(v) => v && setCostCurrency(v as "USD" | "NZD")}
                  variant="outline"
                  size="sm"
                >
                  <ToggleGroupItem value="USD">USD</ToggleGroupItem>
                  <ToggleGroupItem value="NZD">NZD</ToggleGroupItem>
                </ToggleGroup>
                <Select
                  value={costWorkspace}
                  onValueChange={(v) => setCostWorkspace(v as CostWorkspace)}
                >
                  <SelectTrigger className="h-8 w-[220px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COST_WORKSPACE_OPTIONS.map(({ value, label }) => (
                      <SelectItem key={value} value={value} className="text-xs">
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {costLoading ? (
                <div className="space-y-3 py-2">
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <Skeleton className="h-20 rounded-xl" />
                    <Skeleton className="h-20 rounded-xl" />
                    <Skeleton className="h-20 rounded-xl" />
                  </div>
                  <Skeleton className="h-[220px] w-full rounded-xl" />
                  <div className="flex gap-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                </div>
              ) : costError ? (
                <p className="py-8 text-center text-sm text-destructive">{costError}</p>
              ) : !costData || costData.days.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No cost data for this period.</p>
              ) : (
                <>{(() => {
                  const totalCost = costFx(costData.total);
                  const devTotal = costFx(costData.days.reduce((s, d) => s + (d.dev ?? 0), 0));
                  const prodTotal = costFx(costData.days.reduce((s, d) => s + (d.prod ?? 0), 0));
                  const avgPerDay = costData.days.length > 0 ? totalCost / costData.days.length : 0;
                  return (
                    <div className={`grid gap-4 mb-5 ${costWorkspace === "all" ? "grid-cols-4" : "grid-cols-3"}`}>
                      <div className="rounded-xl border border-border bg-muted/40 px-4 py-3">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Total {periodLabel}</p>
                        <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                          {costSymbol}{totalCost.toFixed(2)}
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{costCurrency}</p>
                      </div>
                      {costWorkspace === "all" && (
                        <>
                          <div className="rounded-xl border border-border bg-muted/40 px-4 py-3">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">Development</p>
                            <p className="mt-1 text-2xl font-semibold tabular-nums" style={{ color: "var(--chart-2)" }}>
                              {costSymbol}{devTotal.toFixed(2)}
                            </p>
                            <p className="mt-0.5 text-xs text-muted-foreground">{costCurrency}</p>
                          </div>
                          <div className="rounded-xl border border-border bg-muted/40 px-4 py-3">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">Production</p>
                            <p className="mt-1 text-2xl font-semibold tabular-nums" style={{ color: "var(--chart-4)" }}>
                              {costSymbol}{prodTotal.toFixed(2)}
                            </p>
                            <p className="mt-0.5 text-xs text-muted-foreground">{costCurrency}</p>
                          </div>
                        </>
                      )}
                      <div className="rounded-xl border border-border bg-muted/40 px-4 py-3">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Avg / day</p>
                        <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                          {costSymbol}{avgPerDay.toFixed(2)}
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{costCurrency}</p>
                      </div>
                    </div>
                  );
                })()}
                {costData.workspace !== "all" && !costData.workspacesConfigured[costData.workspace as "dev" | "prod"] && (
                    <p className="mb-3 rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
                      Workspace ID not configured — add{" "}
                      <code className="font-mono">
                        {costData.workspace === "dev" ? "ANTHROPIC_WORKSPACE_DEV_ID" : "ANTHROPIC_WORKSPACE_PROD_ID"}
                      </code>{" "}
                      to Supabase secrets.
                    </p>
                  )}
                  <ChartContainer config={costChartConfig} className="h-55 aspect-auto">
                    <BarChart
                      data={costData.days.map((d) => ({
                        ...d,
                        cost: costFx(d.cost),
                        dev:  d.dev  != null ? costFx(d.dev)  : undefined,
                        prod: d.prod != null ? costFx(d.prod) : undefined,
                      }))}
                      margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                        axisLine={false}
                        tickLine={false}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                        axisLine={false}
                        tickLine={false}
                        width={58}
                        tickFormatter={(v) => `${costSymbol}${v.toFixed(2)}`}
                      />
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            formatter={(value) =>
                              typeof value === "number" ? `${costSymbol}${value.toFixed(4)}` : String(value)
                            }
                          />
                        }
                      />
                      {costWorkspace === "all" ? (
                        <>
                          <Bar dataKey="dev"  fill="var(--color-dev)"  radius={[0, 0, 0, 0]} maxBarSize={40} stackId="cost" />
                          <Bar dataKey="prod" fill="var(--color-prod)" radius={[4, 4, 0, 0]} maxBarSize={40} stackId="cost" />
                        </>
                      ) : (
                        <Bar dataKey="cost" fill="var(--color-cost)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                      )}
                    </BarChart>
                  </ChartContainer>
                  {costWorkspace === "all" && (
                    <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <span className="size-2.5 rounded-sm" style={{ background: "var(--chart-2)" }} />
                        Development
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="size-2.5 rounded-sm" style={{ background: "var(--chart-4)" }} />
                        Production
                      </span>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
}
